import { SITE_URL, GITHUB_URL, LINKEDIN_URL, NPM_PACKAGE_URL, OPENGRAPH_IMAGE } from '@/lib/site';

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': ['Organization', 'SoftwareCompany'],
  '@id': `${SITE_URL}/#organization`,
  name: 'Silker AI',
  alternateName: ['Silker', 'Silker AI Security', 'SilkerAI'],
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/brand/logo.png`,
    width: 1024,
    height: 1024,
  },
  description: 'AI-powered reverse proxy security platform providing runtime protection, automated penetration testing, and comprehensive threat detection for any web application. Deploy as Docker container, no code changes required.',
  foundingDate: '2024',
  slogan: 'Protect your web app. Zero code changes.',
  foundingLocation: {
    '@type': 'Place',
    address: { '@type': 'PostalAddress', addressCountry: 'PL' },
  },
  founder: {
    '@type': 'Person',
    '@id': `${SITE_URL}/#founder`,
    name: 'Marcin Jarzębowski',
    jobTitle: 'Founder',
    worksFor: { '@id': `${SITE_URL}/#organization` },
    sameAs: ['https://x.com/jarzebowsky_dev', LINKEDIN_URL],
  },
  sameAs: [
    'https://x.com/jarzebowsky_dev',
    GITHUB_URL,
    LINKEDIN_URL,
  ],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'Customer Support',
      email: 'support@silkerai.com',
      availableLanguage: ['English'],
    },
    {
      '@type': 'ContactPoint',
      contactType: 'Sales',
      email: 'sales@silkerai.com',
      availableLanguage: ['English'],
    },
  ],
  areaServed: 'Worldwide',
  knowsAbout: [
    'Application Security',
    'Reverse Proxy Security',
    'Penetration Testing',
    'OWASP Top 10',
    'Runtime Application Self-Protection',
    'Web Application Firewall',
    'Threat Detection',
    'Data Leak Prevention',
  ],
}

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: 'Silker AI',
  alternateName: ['Silker', 'Silker AI Security Platform'],
  url: SITE_URL,
  description: 'AI-powered reverse proxy security platform with runtime protection, automated pentesting, and threat detection for any web application',
  publisher: { '@id': `${SITE_URL}/#organization` },
  inLanguage: 'en-US',
}

export const softwareApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  '@id': `${SITE_URL}/#software`,
  name: 'Silker AI Security Agent',
  alternateName: '@silker-ai/agent',
  applicationCategory: 'SecurityApplication',
  applicationSubCategory: 'Runtime Application Security',
  operatingSystem: ['Linux', 'macOS', 'Windows'],
  programmingLanguage: ['TypeScript'],
  runtimePlatform: ['Node.js'],
  offers: [
    {
      '@type': 'Offer',
      name: 'Starter Plan',
      price: '0',
      priceCurrency: 'USD',
      description: 'Free tier - up to 10,000 requests/month',
      availability: 'https://schema.org/InStock',
    },
    {
      '@type': 'Offer',
      name: 'Pro Plan',
      price: '39',
      priceCurrency: 'USD',
      description: 'Professional plan with advanced features',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '39',
        priceCurrency: 'USD',
        billingDuration: 'P1M',
      },
      availability: 'https://schema.org/InStock',
    },
    {
      '@type': 'Offer',
      name: 'Business Plan',
      price: '99',
      priceCurrency: 'USD',
      description: 'Business plan with team features and higher limits',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '99',
        priceCurrency: 'USD',
        billingDuration: 'P1M',
      },
      availability: 'https://schema.org/InStock',
    },
  ],
  description: 'Reverse proxy with AI-powered threat detection, OWASP Top 10 protection, response inspection, automated penetration testing, and real-time security monitoring for any web application.',
  softwareVersion: '1.0.0',
  releaseNotes: 'Reverse proxy with request and response inspection, automatic API schema learning, and PII detection',
  featureList: [
    'Reverse proxy deployment',
    'Real-time threat detection',
    'Response inspection and PII detection',
    'OWASP Top 10 protection',
    'Automated penetration testing',
    'Automatic API schema learning',
    'Rate limiting',
    'Data leakage prevention',
    'Works with any language and framework',
  ],
  author: { '@id': `${SITE_URL}/#organization` },
  publisher: { '@id': `${SITE_URL}/#organization` },
  screenshot: OPENGRAPH_IMAGE,
  downloadUrl: NPM_PACKAGE_URL,
  installUrl: `${SITE_URL}/docs`,
  softwareHelp: {
    '@type': 'CreativeWork',
    url: `${SITE_URL}/docs`,
  },
  softwareRequirements: 'Docker',
}

export const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
})

export const faqSchema = (questions: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: questions.map(q => ({
    '@type': 'Question',
    name: q.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: q.answer,
    },
  })),
})
